import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  RoomProvider,
  useRoomSession,
  useRoomPhase,
  useRoomContext,
  useRoomParticipants,
  useLiveStateSync,
  isValidJoinCode,
  normalizeJoinCode,
  ExamStudentStage,
  mapExamJoinError,
  mapRoomJoinError,
  lookupScheduledExam,
} from '@interactive-room';
import { SalaShell } from '../components/layout/SalaShell';
import { ActivityFrame } from '../components/ui/ActivityFrame';
import { ExamShield } from '../components/ui/ExamShield';
import { useExamIntegrity } from '../hooks/useExamIntegrity';
import { MODE_VISUALS } from '../theme/designSystem';
import type { GameMode } from '@interactive-room';

type JoinKind = 'live' | 'exam';

function JoinContent() {
  const [searchParams] = useSearchParams();
  const initialKind = searchParams.get('mode') === 'exam' ? 'exam' : 'live';
  const initialCode = searchParams.get('code') ?? '';

  const [joinKind, setJoinKind] = useState<JoinKind>(initialKind);
  const [code, setCode] = useState(initialCode);
  const [hint, setHint] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState(() => {
    try {
      return localStorage.getItem('sala_student_name') || '';
    } catch {
      return '';
    }
  });

  const { connected, roomId, joinRoomAsStudent, joinExamByAccessCode, rejoinStoredSession, lastError } =
    useRoomSession();
  const { phase } = useRoomPhase();
  const { state } = useRoomContext();
  const { participants } = useRoomParticipants();

  const examSession = state.state?.examSession;
  const autonomousExam =
    examSession?.status === 'ACTIVE' && examSession.advanceMode === 'AUTONOMOUS';

  const gameMode = autonomousExam
    ? (state.examSlot?.mode as GameMode | undefined)
    : (state.state?.core.activity.mode as GameMode | undefined);
  const visual = gameMode ? MODE_VISUALS[gameMode] : null;
  const assessmentMode = Boolean(state.state?.core.flags?.assessmentMode);
  const me = participants.find((p) => p.participantId === state.participantId);
  const evaluationLocked = Boolean(me?.evaluationLocked);

  const exam = useExamIntegrity({
    displayName: displayName.trim() || 'Estudiante',
    enabled: assessmentMode,
    locked: evaluationLocked,
  });

  const normalizedCode = normalizeJoinCode(code);
  const canJoin = isValidJoinCode(normalizedCode) && displayName.trim().length > 0;

  const errorMessage = useMemo(() => {
    if (!lastError) return null;
    return joinKind === 'exam' ? mapExamJoinError(lastError) : mapRoomJoinError(lastError);
  }, [joinKind, lastError]);

  const handleCodeBlur = () => {
    if (!isValidJoinCode(normalizedCode)) {
      setHint(null);
      return;
    }
    void lookupScheduledExam(normalizedCode).then((info) => {
      if (info) {
        setJoinKind('exam');
        setHint(`${info.title} · ${info.totalBlocks} bloques`);
        return;
      }
      setJoinKind('live');
      setHint(null);
    });
  };

  const handleJoin = async () => {
    if (!canJoin) return;
    try {
      localStorage.setItem('sala_student_name', displayName.trim());
    } catch {
      /* ignore */
    }
    const examInfo = await lookupScheduledExam(normalizedCode);
    if (examInfo || joinKind === 'exam') {
      setJoinKind('exam');
      void joinExamByAccessCode({ accessCode: normalizedCode, displayName: displayName.trim() });
      return;
    }
    void joinRoomAsStudent({ joinCode: normalizedCode, displayName: displayName.trim() });
  };

  useLiveStateSync(Boolean(roomId));

  /* Sesión activa: cubre toda la vista (campo). Sin acceso a Studio. */
  if (roomId) {
    return (
      <div className="sala-join-session-root">
        <SalaShell variant="field" phase={phase} mode={gameMode ?? null} bare>
          <div
            className="flex min-h-dvh flex-col overflow-hidden px-[var(--sala-gutter)] pb-[var(--sala-gutter)]"
            style={
              visual
                ? {
                    ['--sala-accent' as string]: visual.hue,
                    ['--sala-accent-neon' as string]: visual.deep,
                    ['--sala-accent-glow' as string]: `${visual.hue}33`,
                  }
                : undefined
            }
          >
            <div className="flex items-center justify-between border-b border-[var(--sala-field-border)] py-3">
              <div>
                <p className="font-display text-lg font-bold text-[var(--sala-field-ink)]">{displayName}</p>
                <p className="sala-label-field">
                  {autonomousExam && examSession
                    ? `${examSession.title} · Bloque ${(state.examSlot?.blockIndex ?? 0) + 1}/${examSession.totalBlocks}`
                    : (visual?.label ?? 'En la sala')}
                </p>
              </div>
              <span className="sala-kicker text-[var(--sala-accent)]">{phase}</span>
            </div>

            <div className="flex min-h-0 flex-1 flex-col py-2">
              {errorMessage && (
                <p className="mb-3 rounded-xl border-2 border-red-300 bg-red-50 px-4 py-2 text-center text-sm font-medium text-red-900">
                  {errorMessage}
                </p>
              )}
              <ExamShield
                locked={evaluationLocked}
                assessmentActive={assessmentMode && phase === 'ACTIVE'}
                fullscreenOk={exam.fullscreenOk}
                blurFlash={exam.blurFlash}
                watermarkLabel={exam.watermarkLabel}
                onEnterFullscreen={exam.enterFullscreen}
              >
                {autonomousExam ? (
                  <ExamStudentStage />
                ) : (
                  <ActivityFrame
                    role="student"
                    phase={phase}
                    mode={gameMode}
                    surface="field"
                    fullscreen
                  />
                )}
              </ExamShield>
            </div>
          </div>
        </SalaShell>
      </div>
    );
  }

  /* Lobby Micelio: solo unirse (sin Studio ni tabs). */
  return (
    <div className="sala-login">
      <div className="sala-login__panel">
        <Link to="/" className="sala-login__back">
          ← Volver
        </Link>

        <p className="sala-login__eyebrow">Unirse</p>
        <h1 className="sala-login__title">Entra a la sala</h1>
        <p className="sala-login__lead">
          Escribe el código que ves en pantalla y tu nombre. Si es una evaluación, lo detectamos solos.
        </p>

        <form
          className="sala-login__form"
          onSubmit={(event) => {
            event.preventDefault();
            void handleJoin();
          }}
        >
          <label className="sala-login__label">
            Código
            <input
              className="sala-input-dark mt-1.5 w-full text-center font-display text-2xl font-bold uppercase tracking-[0.28em]"
              value={code}
              maxLength={6}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onBlur={handleCodeBlur}
              placeholder="ABC123"
              autoComplete="off"
              inputMode="text"
              required
            />
          </label>

          {hint ? <p className="sala-login__hint">{hint}</p> : null}

          <label className="sala-login__label">
            Tu nombre
            <input
              className="sala-input-dark mt-1.5 w-full"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="María García"
              autoComplete="nickname"
              required
            />
          </label>

          {errorMessage ? (
            <p className="sala-login__error" role="alert">
              {errorMessage}
            </p>
          ) : null}

          <button type="submit" className="sala-landing-cta w-full" disabled={!canJoin}>
            {canJoin ? 'Unirme' : 'Completa código y nombre'}
          </button>

          {connected ? (
            <button
              type="button"
              className="sala-login__switch-btn text-center text-sm"
              onClick={() => void rejoinStoredSession()}
            >
              Reconectar sesión anterior
            </button>
          ) : (
            <button
              type="button"
              className="sala-login__ghost"
              onClick={() => void rejoinStoredSession()}
            >
              Reconectar sesión anterior
            </button>
          )}
        </form>
      </div>
    </div>
  );
}

export function JoinPage() {
  return (
    <RoomProvider>
      <JoinContent />
    </RoomProvider>
  );
}
